import { Component, input, output, signal, inject, forwardRef, ChangeDetectionStrategy } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { SingleFileUploadComponent } from 'src/app/controls/components/basic/single-file-upload/single-file-upload.component';
import { LinkComponent } from '../link/link.component';

@Component({
  selector: 'app-image-uploader',
  standalone: true,
  imports: [SingleFileUploadComponent, LinkComponent],
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploaderComponent),
      multi: true
    }
  ]
})
export class ImageUploaderComponent implements ControlValueAccessor {
  private toastService = inject(ToastService);

  /* Inputs */
  caption = input<string>('Upload Image');

  /** Allowed MIME types (e.g. ['image/jpeg','image/png']). Empty/null => no restriction. */
  allowedTypes = input<string[]>([]);

  /** Max file size in bytes. Null => no limit. */
  maxSizeBytes = input<number | null>(null);

  /** Dimension constraints in px. Null => not enforced. */
  minWidth = input<number | null>(null);
  minHeight = input<number | null>(null);
  maxWidth = input<number | null>(null);
  maxHeight = input<number | null>(null);

  /** Aspect ratio validation. */
  enforceAspectRatio = input<boolean>(false);
  targetAspectRatio = input<number | null>(null);
  aspectRatioTolerance = input<number>(0.1);

  showPreview = input<boolean>(true);
  showRemoveButton = input<boolean>(true);
  showValidationText = input<boolean>(true);
  acceptAttribute = input<string>('image/*');

  /* Outputs */
  imageChanged = output<string | null>();
  imageRemoved = output<void>();
  validationError = output<string>();

  currentImagePreview = signal<string | null>(null);
  disabled = false;

  /* CVA callbacks */
  private onChanged: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  /* =======================
     ControlValueAccessor
     ======================= */

  writeValue(value: string | null): void {
    this.currentImagePreview.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /* =======================
     Upload handling
     ======================= */

  onFileSelected(file: File): void {
    if (!file || this.disabled) return;

    const allowed = this.allowedTypes();
    if (allowed?.length && !allowed.includes(file.type)) {
      this.fail(`Please select a valid image (${this.getAllowedTypesText()})`);
      return;
    }

    const maxBytes = this.maxSizeBytes();
    if (maxBytes != null && file.size > maxBytes) {
      const maxMB = (maxBytes / (1024 * 1024)).toFixed(1);
      this.fail(`Image must be smaller than ${maxMB}MB`);
      return;
    }

    const needsDimensionValidation =
      this.minWidth() != null ||
      this.minHeight() != null ||
      this.maxWidth() != null ||
      this.maxHeight() != null;

    const needsAspectValidation =
      this.enforceAspectRatio() && this.targetAspectRatio() != null;

    if (needsDimensionValidation || needsAspectValidation) {
      this.validateDimensionsAndConvert(file);
    } else {
      this.convertToBase64(file);
    }
  }

  removeImage(): void {
    if (this.disabled) return;

    this.currentImagePreview.set(null);
    this.onChanged(null);
    this.imageChanged.emit(null);
    this.imageRemoved.emit();
  }

  /* =======================
     Validation helpers
     ======================= */

  private validateDimensionsAndConvert(file: File): void {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const dimError = this.validateDimensions(img);
      if (dimError) return this.fail(dimError);

      if (this.enforceAspectRatio()) {
        const ratio = this.targetAspectRatio();
        if (ratio != null) {
          const tol = this.aspectRatioTolerance() ?? 0.1;
          const arError = this.validateAspectRatio(img, ratio, tol);
          if (arError) return this.fail(arError);
        }
      }

      this.convertToBase64(file);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      this.fail('Failed to load image');
    };

    img.src = url;
  }

  private convertToBase64(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      const value = reader.result as string;
      this.currentImagePreview.set(value);
      this.onChanged(value);
      this.imageChanged.emit(value);
    };

    reader.onerror = () => this.fail('Failed to read file');

    reader.readAsDataURL(file);
  }

  private validateDimensions(image: HTMLImageElement): string | null {
    const maxW = this.maxWidth();
    if (maxW != null && image.width > maxW) return `Max width ${maxW}px`;

    const maxH = this.maxHeight();
    if (maxH != null && image.height > maxH) return `Max height ${maxH}px`;

    const minW = this.minWidth();
    if (minW != null && image.width < minW) return `Min width ${minW}px`;

    const minH = this.minHeight();
    if (minH != null && image.height < minH) return `Min height ${minH}px`;

    return null;
  }

  private validateAspectRatio(image: HTMLImageElement, ratio: number, tolerance: number): string | null {
    const r = image.width / image.height;
    return Math.abs(r - ratio) > tolerance
      ? `Aspect ratio must be ${ratio}:1`
      : null;
  }

  private fail(message: string): void {
    this.toastService.showToast(message, ToastType.error);
    this.validationError.emit(message);
  }

  private getAllowedTypesText(): string {
    return this.allowedTypes()
      ?.map(t => t.split('/')[1].toUpperCase())
      .join(', ') ?? '';
  }
}
